// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Human approval surface for a high-impact action.
 *
 * `ARCH-026` and `BUS-005` require the reviewer to see the exact proposed
 * action, its target, the material inputs, the side effects, and the supporting
 * evidence before deciding. `ui/docs/Implementation.md` §5 repeats this as a
 * module definition-of-done item. Everything mandated is rendered here, and the
 * decision controls are disabled until the reviewer records a justification.
 *
 * Enforcement stays on the server. `TST-026` requires that approval cannot be
 * bypassed through the UI, and `AIM-001` states that narration is not proof of
 * action: this component collects a decision, it never performs one. The
 * payload digest the reviewer actually saw is submitted with the decision so a
 * changed payload is rejected deterministically by the service.
 */

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useId, useState, type ReactNode } from 'react';

import type { ApprovalRequest } from '../api-clients/views';
import { AiLimitations, CitationList } from './CitationList';
import { DataTable } from './DataTable';
import { StateBadge } from './StatusBadge';

/** Minimum justification length accepted by the panel. */
export const MIN_JUSTIFICATION_LENGTH = 10;

/** A decision the reviewer can record. */
export type ApprovalDecision = 'approve' | 'reject';

/** Props for {@link ApprovalPanel}. */
export interface ApprovalPanelProps {
  /** The request under review. */
  readonly approval: ApprovalRequest;
  /** Whether the signed-in account was offered the approval capability. */
  readonly canDecide: boolean;
  /** Whether a decision is currently being submitted. */
  readonly submitting?: boolean;
  /** Invoked with the reviewer's decision, digest, and justification. */
  readonly onDecide: (input: {
    decision: ApprovalDecision;
    payloadDigest: string;
    justification: string;
  }) => void;
}

const STATE_TONE = {
  pending: 'caution',
  approved: 'positive',
  rejected: 'negative',
  expired: 'neutral',
  cancelled: 'neutral',
} as const;

/** Present a high-impact action for human decision. */
export function ApprovalPanel({
  approval,
  canDecide,
  submitting = false,
  onDecide,
}: ApprovalPanelProps): ReactNode {
  const [justification, setJustification] = useState('');
  const justificationId = useId();
  const headingId = useId();

  const justificationValid = justification.trim().length >= MIN_JUSTIFICATION_LENGTH;
  const decidable = canDecide && approval.state === 'pending' && !submitting;

  const submit = (decision: ApprovalDecision): void => {
    onDecide({
      decision,
      payloadDigest: approval.payload_digest,
      justification: justification.trim(),
    });
  };

  return (
    <Card component="section" aria-labelledby={headingId} variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Typography id={headingId} variant="h2" component="h2">
            {approval.action}
          </Typography>
          <StateBadge
            label={approval.state}
            tone={STATE_TONE[approval.state]}
            subject="Approval state"
          />
        </Stack>

        <Typography variant="body1" sx={{ mt: 1 }}>
          Target: {approval.target_label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Requested by {approval.requested_by} at {approval.requested_at}
          {approval.expires_at === null ? '' : ` · expires ${approval.expires_at}`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Policy version {approval.policy_version} · payload digest {approval.payload_digest}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h3" component="h3" gutterBottom>
          Material inputs
        </Typography>
        {approval.material_inputs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            The service reported no material inputs for this action.
          </Typography>
        ) : (
          <DataTable
            caption={`Material inputs for ${approval.action}`}
            columns={[
              { key: 'label', header: 'Input', render: (row) => row.label },
              { key: 'value', header: 'Value', render: (row) => row.value },
            ]}
            rows={approval.material_inputs}
            rowKey={(row) => row.label}
          />
        )}

        <Typography variant="h3" component="h3" sx={{ mt: 2 }} gutterBottom>
          Side effects
        </Typography>
        {approval.side_effects.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            The service reported no side effects for this action.
          </Typography>
        ) : (
          <ul>
            {approval.side_effects.map((effect) => (
              <li key={effect}>
                <Typography variant="body2">{effect}</Typography>
              </li>
            ))}
          </ul>
        )}

        <Divider sx={{ my: 2 }} />

        <CitationList citations={approval.evidence} heading="Supporting evidence" />
        <AiLimitations limitations={approval.ai_limitations} />

        <Divider sx={{ my: 2 }} />

        {approval.state === 'pending' ? (
          <Stack spacing={2}>
            <TextField
              id={justificationId}
              label="Decision justification"
              helperText={`Recorded in the audit trail. Minimum ${String(MIN_JUSTIFICATION_LENGTH)} characters.`}
              value={justification}
              onChange={(event) => {
                setJustification(event.target.value);
              }}
              multiline
              minRows={2}
              required
              disabled={!decidable}
              error={justification.length > 0 && !justificationValid}
              slotProps={{ htmlInput: { maxLength: 2000 } }}
            />
            {canDecide ? null : (
              <Typography variant="body2" color="text.secondary">
                Your account was not offered the approval capability for this action. The service
                authorises each decision independently.
              </Typography>
            )}
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                disabled={!decidable || !justificationValid}
                onClick={() => {
                  submit('approve');
                }}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                disabled={!decidable || !justificationValid}
                onClick={() => {
                  submit('reject');
                }}
              >
                Reject
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Typography variant="body2">
            Decided {approval.decided_at ?? 'at an unrecorded time'}
            {approval.decided_by === null ? '' : ` by ${approval.decided_by}`}.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
